"use client";

import React from "react";
import { Leaf, Sparkles, Loader2, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function MembershipForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div data-section-id="120755" ref={ref} className={`flex flex-col items-center justify-center p-8 text-center space-y-4 bg-primary/5 border border-primary/10 rounded-sm transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="size-12 rounded-sm bg-primary/10 flex items-center justify-center mb-2">
          <Sparkles className="size-6 text-primary" />
        </div>
        <h3 className="text-2xl font-serif text-foreground">Anmälan mottagen</h3>
        <p className="text-muted-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 rounded-sm">
          Skicka ny anmälan
        </Button>
      </div>;
  }
  return <div ref={ref} className={`transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <Form onSubmit={handleSubmit} className="space-y-5">
        <FormField control={control} name="full_name" rules={{
        required: "Namn är obligatoriskt"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Fullständigt namn</FormLabel>
              <FormControl>
                <Input placeholder="Erik Johansson" className="rounded-sm border-input" {...field} />
              </FormControl>
              <FormDescription>Ange ditt fullständiga namn.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-postadress är obligatorisk",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Ogiltig e-postadress"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">E-postadress</FormLabel>
              <FormControl>
                <Input type="email" placeholder="erik.johansson@exempel.se" className="rounded-sm border-input" {...field} />
              </FormControl>
              <FormDescription>
                Vi skickar viktig information till denna adress.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="070-123 45 67" className="rounded-sm border-input" {...field} />
              </FormControl>
              <FormDescription>Valfritt, för snabbare återkoppling.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="interests" render={({
        field
      }) => {
        const options = ["Robotaxi", "Logistik", "Underhåll", "Kundsupport"];
        return <FormItem>
                <FormLabel className="font-sans">Vilka områden intresserar dig?</FormLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  {options.map((option, i) => <label key={option} data-index={i} className="flex items-center space-x-3 space-y-0 rounded-sm border border-input bg-card p-3 hover:bg-accent transition-colors cursor-pointer">
                      <input type="checkbox" className="size-4 rounded-sm border-primary text-primary focus:ring-primary/50 bg-transparent" checked={field.value?.includes(option)} onChange={e => {
                const current = field.value || [];
                const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
                field.onChange(updated);
              }} />
                      <span className="text-sm font-medium leading-none text-foreground">
                        {option}
                      </span>
                    </label>)}
                </div>
                <FormDescription>
                  Välj en eller flera kategorier.
                </FormDescription>
                <FormMessage />
              </FormItem>;
      }} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans">Övrig information (valfritt)</FormLabel>
              <FormControl>
                <textarea className="flex w-full min-w-0 rounded-sm border border-input bg-transparent px-3 py-2 text-base shadow-sm transition-[color,box-shadow] outline-none focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[2px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm min-h-[100px] resize-y" placeholder="Berätta gärna kort om din bakgrund eller ställ frågor..." {...field} />
              </FormControl>
              <FormDescription>
                Dela dina tankar eller specifika önskemål.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm text-destructive text-sm">
            {submitError}
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-medium rounded-sm bg-primary text-primary-foreground shadow-md hover:shadow-lg transition-all duration-300">
          {isSubmitting ? <>
              <Loader2 className="animate-spin mr-2 size-5" />
              Skickar...
            </> : <>
              Skicka intresseanmälan
              <Send className="ml-2 size-4" />
            </>}
        </Button>
      </Form>
    </div>;
}
export default function JoinOurCommunity() {
  const {
    ref: leftRef,
    isVisible: leftIsVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="bli-operatör-anmäl-dig" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-5">
        <svg className="absolute -top-24 -left-24 w-96 h-96 text-primary transform -rotate-12" viewBox="0 0 200 200" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M45.7,-76.2C58.9,-69.3,69.2,-55.4,77.2,-40.5C85.2,-25.6,90.9,-9.7,88.5,5.3C86.1,20.3,75.6,34.4,64.3,46.4C53,58.4,40.9,68.3,26.7,74.5C12.5,80.7,-3.8,83.2,-19.4,79.8C-35,76.4,-49.9,67.1,-61.5,54.5C-73.1,41.9,-81.4,26,-84.5,9.3C-87.6,-7.4,-85.5,-24.9,-76.8,-39.1C-68.1,-53.3,-52.8,-64.2,-38.1,-70.5C-23.4,-76.8,-9.3,-78.5,6.1,-86.8C21.5,-95.1,32.5,-83.1,45.7,-76.2Z" transform="translate(100 100)" />
        </svg>
        <svg className="absolute -bottom-32 -right-32 w-[500px] h-[500px] text-primary transform rotate-45" viewBox="0 0 200 200" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M39.9,-65.5C53.1,-58.1,66.2,-49.6,74.8,-37.4C83.4,-25.2,87.5,-9.3,85.1,5.6C82.7,20.5,73.8,34.4,62.6,45.1C51.4,55.8,37.9,63.3,23.4,68.5C8.9,73.7,-6.6,76.6,-21.4,73.5C-36.2,70.4,-50.3,61.3,-61.1,49.2C-71.9,37.1,-79.4,22,-81.5,6.3C-83.6,-9.4,-80.3,-25.7,-71.5,-38.6C-62.7,-51.5,-48.4,-61,-34.3,-67.6C-20.2,-74.2,-6.3,-77.9,6.5,-86.8C19.3,-95.7,32.1,-89.8,39.9,-65.5Z" transform="translate(100 100)" />
        </svg>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {}
          <div ref={leftRef} className={`space-y-8 transition-all duration-1000 ${leftIsVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-sm bg-accent border border-border text-accent-foreground text-sm font-medium">
              <Sparkles className="size-4" />
              <span>Operatörsprogrammet</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-foreground tracking-tight">
              Bli en del av framtidens mobilitet
            </h2>
            
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl font-sans">
              Registrera dig som operatör för vår växande flotta. Vi söker engagerade partners som vill vara med och forma morgondagens transportlösningar med hållbar teknik och innovation.
            </p>

            <div className="pt-4 flex flex-col sm:flex-row gap-6 text-sm text-muted-foreground font-sans">
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-sm bg-primary/5 flex items-center justify-center">
                  <Leaf className="size-5 text-primary" />
                </div>
                <span>Hållbar utveckling</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="size-10 rounded-sm bg-primary/5 flex items-center justify-center">
                  <Sparkles className="size-5 text-primary" />
                </div>
                <span>Framtidens teknik</span>
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            {}
            <div className="absolute inset-0 bg-primary/5 blur-[80px] rounded-full pointer-events-none" />
            
            <div className="relative bg-card border border-border rounded-sm p-6 md:p-10 shadow-xl">
              <div className="mb-8">
                <h3 className="text-2xl font-serif text-foreground mb-2 tracking-tight">
                  Anmäl ditt intresse idag
                </h3>
                <p className="text-muted-foreground text-sm font-sans">
                  Fyll i dina uppgifter nedan för att påbörja din resa som operatör.
                </p>
              </div>

              <FormProvider formKey="operator_interest_form_robotaxiframstegochaffrsfall" formId="operator_interest_form_robotaxiframstegochaffrsfall" sectionName="JoinOurCommunity" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              interests: [],
              message: ""
            }} submitText="Skicka intresseanmälan" successMessage="Tack för din anmälan! Vårt team kommer att granska dina uppgifter och återkomma till dig inom kort för nästa steg." errorMessage="Ett fel uppstod när formuläret skulle skickas. Vänligen kontrollera dina uppgifter och försök igen.">
                <MembershipForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}